import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ClipboardList, FolderOpen, TrendingUp, Archive, ArrowLeft, LogOut, Bell, Plus, Save, ChevronDown, ChevronUp, UserCog, Calculator, Package, BarChart3, Cog } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import LabelPrinting from "@/components/LabelPrinting";
import ThemeToggle from "@/components/ThemeToggle";
import { useModuleAuth } from "@/hooks/useModuleAuth";
import { AuthCheckSkeleton } from "@/components/AuthCheckSkeleton";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Import the page components
import WorkOrderCreationNew from "@/pages/WorkOrderCreationNew";
import SavedWorkOrders from "@/pages/SavedWorkOrders";
import WorkOrderProgress from "@/pages/WorkOrderProgress";
import WorkOrderArchive from "@/pages/WorkOrderArchive";
import CustomerDatabase from "@/pages/CustomerDatabase";
import WorkOrderQuotation from "@/pages/WorkOrderQuotation";
import ProdMaterialInfo from "@/pages/ProdMaterialInfo";
import WOReports from "@/pages/WOReports";
import Machines from "@/pages/Machines";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-scope constant so reference stays stable across renders and
// hooks that depend on it don't get flagged for missing deps.
const TABS = [
  { id: "creation", label: "WO Creation", icon: ClipboardList },
  { id: "saved", label: "Saved WOs", icon: FolderOpen },
  { id: "progress", label: "WO Progress", icon: TrendingUp },
  { id: "database", label: "WO Database", icon: UserCog },
  { id: "quotation", label: "Quotation", icon: Calculator },
  { id: "prod-info", label: "Prod Info", icon: Package },
  { id: "reports", label: "Reports", icon: BarChart3 },
  { id: "archive", label: "Archive", icon: Archive },
  { id: "machines", label: "Machines", icon: Cog },
];

const WorkOrderUnified = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Pro-only per-module auth wall (no-op on Core).
  const checking = useModuleAuth({ role: "workorder", loginPath: "/workorder-login" });
  
  // Active tab state - default to 'saved' (Saved Work Orders)
  const [activeTab, setActiveTab] = useState("saved");
  
  // Notification states
  const [showNotifications, setShowNotifications] = useState(false);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [expandedWO, setExpandedWO] = useState(null);
  const [commitments, setCommitments] = useState({});
  const [newEntry, setNewEntry] = useState({ date: "", comment: "" });
  const [savingDelivery, setSavingDelivery] = useState(false);

  const tabs = TABS;

  // Check URL hash for initial tab
  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash && TABS.some(t => t.id === hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Update URL hash when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    window.history.replaceState(null, "", `#${tabId}`);
  };

  // Fetch completed orders for notifications
  const fetchCompletedOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/work-orders`);
      const completed = response.data.filter(wo => wo.status === "completed");
      setCompletedOrders(completed);
      setNotificationCount(completed.length);
    } catch (error) {
      console.error("Error fetching completed orders:", error);
    }
  }, []);

  const fetchCommitments = async (woNumber) => {
    try {
      const res = await axios.get(`${API}/work-orders/${woNumber}/delivery-commitments`);
      setCommitments(prev => ({ ...prev, [woNumber]: res.data.commitments || [] }));
    } catch (err) {
      setCommitments(prev => ({ ...prev, [woNumber]: [] }));
    }
  };

  const toggleWOExpand = (woNumber) => {
    if (expandedWO === woNumber) {
      setExpandedWO(null);
    } else {
      setExpandedWO(woNumber);
      setNewEntry({ date: "", comment: "" });
      fetchCommitments(woNumber);
    }
  };

  const saveNewCommitment = async (woNumber) => {
    if (!newEntry.date) {
      toast.error("Please select a delivery date");
      return;
    }
    setSavingDelivery(true);
    try {
      await axios.patch(`${API}/work-orders/${woNumber}/committed-delivery`, {
        committed_delivery_date: newEntry.date,
        delivery_comment: newEntry.comment,
      });
      toast.success("Delivery commitment added");
      setNewEntry({ date: "", comment: "" });
      fetchCommitments(woNumber);
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSavingDelivery(false);
    }
  };

  useEffect(() => {
    // Auth removed: page is openly accessible.
    fetchCompletedOrders();
    const interval = setInterval(fetchCompletedOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchCompletedOrders]);

  const handleLogout = async () => {
    try { const { logout: doLogout } = await import("@/utils/auth"); await doLogout(); } catch (err) { console.error("Logout cleanup failed:", err); }
    localStorage.removeItem("quotation_page_auth");
    toast.success("Logged out successfully");
    navigate("/");
  };

  // Render the active component based on selected tab
  const renderActiveComponent = () => {
    switch (activeTab) {
      case "creation":
        return <WorkOrderCreationNew />;
      case "saved":
        return <SavedWorkOrders />;
      case "progress":
        return <WorkOrderProgress />;
      case "archive":
        return <WorkOrderArchive />;
      case "database":
        return <CustomerDatabase />;
      case "quotation":
        return <WorkOrderQuotation />;
      case "prod-info":
        return <ProdMaterialInfo />;
      case "reports":
        return <WOReports />;
      case "machines":
        return <Machines />;
      default:
        return <SavedWorkOrders />;
    }
  };

  if (checking) return <AuthCheckSkeleton />;

  return (
    <div className="min-h-screen workorder-theme flex flex-col">
      {/* Navigation Header */}
      <nav className="bg-black/90 shadow-2xl border-b-2 border-slate-500/30 backdrop-blur-sm relative">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-500/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-6">
              {/* Logo */}
              <div className="cursor-pointer" onClick={() => handleTabChange("saved")}>
                <Logo size="md" showText={false} />
              </div>
              
              {/* Title */}
              <h1 className="text-xl font-bold text-cyan-400 uppercase tracking-wider">
                Work Order
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Label Printing Button */}
              <LabelPrinting />
              
              {/* Theme Toggle */}
              <ThemeToggle />
              
              {/* Notification Bell Icon */}
              <button
                onClick={() => setShowNotifications(true)}
                className="relative p-2 text-cyan-400 hover:text-cyan-300 transition-all"
                data-testid="notification-bell"
              >
                <Bell className="h-6 w-6" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                    {notificationCount}
                  </span>
                )}
              </button>

              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-slate-500/50 text-cyan-400 hover:bg-cyan-500 hover:text-black font-bold uppercase tracking-wide transition-all hover:shadow-[0_0_20px_rgba(34,211,238,0.3)]"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate("/")}
                className="border-slate-500/50 text-cyan-400 hover:bg-cyan-500 hover:text-black font-bold uppercase tracking-wide transition-all hover:shadow-[0_0_20px_rgba(34,211,238,0.3)]"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Home
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Tab Toggle Buttons */}
      <div className="bg-black/80 border-b border-slate-500/20 px-4 py-3">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <Button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  variant={isActive ? "default" : "outline"}
                  className={`flex items-center gap-2 transition-all duration-200 ${
                    isActive
                      ? "bg-cyan-600 hover:bg-cyan-700 text-white border-cyan-500 shadow-[0_0_15px_rgba(34,211,238,0.3)]"
                      : "bg-transparent border-slate-500/50 text-gray-400 hover:text-cyan-400 hover:border-cyan-400/50"
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-semibold">{tab.label}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto">
        {renderActiveComponent()}
      </main>

      {/* Notification Modal */}
      <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
        <DialogContent className="sm:max-w-[600px] bg-black border-2 border-slate-500/50">
          <DialogHeader>
            <DialogTitle className="text-2xl text-cyan-400 flex items-center gap-2">
              <Bell className="h-6 w-6" />
              Completed Orders Ready for Delivery
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {notificationCount} work order(s) completed and ready for delivery
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto">
            {completedOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No completed orders at the moment
              </div>
            ) : (
              completedOrders.map((order) => {
                const woNum = order.header?.work_order_number;
                const isExpanded = expandedWO === woNum;
                const woCommitments = commitments[woNum] || [];
                
                return (
                  <div
                    key={order.id}
                    className="bg-green-500/10 border border-green-500/30 rounded-lg overflow-hidden"
                  >
                    <div className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white">
                            {woNum}
                          </h3>
                          <p className="text-sm text-gray-400">
                            Customer: {order.header?.customer_name}
                          </p>
                          <p className="text-sm text-gray-400">
                            Item: {order.header?.item_name} | Qty: {order.header?.required_quantity}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="inline-block px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                            COMPLETED
                          </span>
                          <button
                            onClick={() => toggleWOExpand(woNum)}
                            className="p-1.5 rounded hover:bg-white/10 transition-colors text-cyan-400"
                            data-testid={`delivery-dropdown-${woNum}`}
                          >
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="border-t border-green-500/20 p-4 space-y-3 bg-black/30">
                        {/* Add New Entry */}
                        <div className="p-3 border border-cyan-500/30 rounded-lg bg-cyan-500/5 space-y-2">
                          <p className="text-xs font-semibold text-cyan-400 flex items-center gap-1">
                            <Plus className="h-3 w-3" /> Add New Committed Delivery
                          </p>
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <label className="text-xs text-gray-400 mb-1 block">Date</label>
                              <input
                                type="date"
                                value={newEntry.date}
                                onChange={(e) => setNewEntry(prev => ({ ...prev, date: e.target.value }))}
                                className="w-full px-2 py-1.5 bg-black/50 border border-gray-600 rounded text-white text-sm focus:border-cyan-400 focus:outline-none [color-scheme:dark]"
                                data-testid={`new-delivery-date-${woNum}`}
                              />
                            </div>
                            <div className="flex-1">
                              <label className="text-xs text-gray-400 mb-1 block">Comment</label>
                              <input
                                type="text"
                                value={newEntry.comment}
                                onChange={(e) => setNewEntry(prev => ({ ...prev, comment: e.target.value }))}
                                placeholder="Delivery notes..."
                                className="w-full px-2 py-1.5 bg-black/50 border border-gray-600 rounded text-white text-sm focus:border-cyan-400 focus:outline-none"
                                data-testid={`new-delivery-comment-${woNum}`}
                              />
                            </div>
                            <Button
                              size="sm"
                              onClick={() => saveNewCommitment(woNum)}
                              disabled={savingDelivery}
                              className="bg-cyan-600 hover:bg-cyan-700 text-white h-8 px-3"
                              data-testid={`save-commitment-${woNum}`}
                            >
                              <Save className="h-3 w-3 mr-1" />
                              Add
                            </Button>
                          </div>
                        </div>

                        {/* Commitments Table */}
                        {woCommitments.length > 0 ? (
                          <div className="rounded-lg overflow-hidden border border-gray-700">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-gray-800/80 text-gray-400">
                                  <th className="text-left px-3 py-2 text-xs font-semibold">#</th>
                                  <th className="text-left px-3 py-2 text-xs font-semibold">Committed Date</th>
                                  <th className="text-left px-3 py-2 text-xs font-semibold">Comment</th>
                                  <th className="text-left px-3 py-2 text-xs font-semibold">Added On</th>
                                </tr>
                              </thead>
                              <tbody>
                                {woCommitments.slice().reverse().map((c, idx) => (
                                  <tr key={idx} className="border-t border-gray-700/50 hover:bg-gray-800/30">
                                    <td className="px-3 py-2 text-gray-500">{woCommitments.length - idx}</td>
                                    <td className="px-3 py-2 text-cyan-400 font-medium">
                                      {c.date ? new Date(c.date).toLocaleDateString() : "-"}
                                    </td>
                                    <td className="px-3 py-2 text-gray-300">{c.comment || "-"}</td>
                                    <td className="px-3 py-2 text-gray-500 text-xs">
                                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : "-"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 text-center py-2">No delivery commitments yet</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkOrderUnified;
